"use client";

import { ALGOLIA_HITS_PER_PAGE, ALGOLIA_PRODUCTS_INDEX } from "@raumagarn/algolia";
import type { AlgoliaProductCard } from "@raumagarn/formatters";
import { useQuery } from "@tanstack/react-query";
import { tryCatch } from "@trydig/utils";
import { algoliaClient, analyticsClient } from "~/features/algolia/client";

const fallbackResponse = async (limit: number) => {
	const [data, error] = await tryCatch(
		algoliaClient.searchSingleIndex<AlgoliaProductCard>({
			indexName: ALGOLIA_PRODUCTS_INDEX,
			searchParams: {
				query: "",
				hitsPerPage: limit,
			},
		}),
	);

	if (error) {
		return [];
	}

	return data.hits;
};

export const useAlgoliaPopularProducts = (limit = ALGOLIA_HITS_PER_PAGE) => {
	return useQuery({
		queryKey: ["popular-products", limit],
		queryFn: async (): Promise<AlgoliaProductCard[]> => {
			// Fetch a buffer beyond `limit`: some of the top-clicked objectIDs may no
			// longer exist in the index (discontinued/removed products), and a single
			// missing one shouldn't sink the whole result down to the fallback.
			const analyticsLimit = limit + 10;

			const [data, error] = await tryCatch(
				analyticsClient.getTopHits({
					index: ALGOLIA_PRODUCTS_INDEX,
					limit: analyticsLimit,
					clickAnalytics: true,
				}),
			);

			if (error || !data?.hits?.length) {
				return fallbackResponse(limit);
			}

			// `count` alone only reflects how often a product appeared as a search
			// result (impressions), which mirrors default ranking rather than actual
			// user behavior. Once click events are tracked (see `insights` on
			// InstantSearch + `sendEvent` on product clicks), rank by clicks instead.
			const sortedHits = [...data.hits].sort((a, b) => {
				const clicksA = "clickCount" in a ? a.clickCount : 0;
				const clicksB = "clickCount" in b ? b.clickCount : 0;
				return clicksB - clicksA || b.count - a.count;
			});

			const objectIDs = sortedHits.map((hit) => hit.hit);

			const [productsData, productsError] = await tryCatch(
				algoliaClient.getObjects<AlgoliaProductCard>({
					requests: objectIDs.map((objectID) => ({
						indexName: ALGOLIA_PRODUCTS_INDEX,
						objectID,
					})),
				}),
			);

			if (productsError || !productsData) {
				return fallbackResponse(limit);
			}

			// `getObjects` returns `null` for objectIDs that no longer exist instead
			// of failing the whole batch — drop those rather than falling back.
			const products = productsData.results.filter(Boolean) as AlgoliaProductCard[];

			return products.length ? products.slice(0, limit) : fallbackResponse(limit);
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});
};
